import { useMutation } from '@tanstack/react-query';
import {
  Activity,
  BookOpen,
  ListFilter,
  Lock,
  LucideIcon,
  Pencil,
  ShoppingBasket,
  Trash2,
  Zap,
} from 'lucide-react';
import { ComponentType, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { IndexPath } from '@/Config/app/router/index.path';
import {
  ActionDto,
  AIActionUsageStatsDto,
  AppActionDto,
  PatchActionDto,
} from '@/Domain/api/Action/dto/Action.dto';
import { ActionBoxIcon } from '@/Domain/components/Action/ActionBox/ActionBoxIcon';
import { ActionLogsSheet } from '@/Domain/components/Action/ActionLogs/ActionLogs.sheet';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { McpIcon } from '@/Domain/components/icons/Mcp.icon';
import { ToolIcon } from '@/Domain/components/icons/Tool.icon';
import { ActionTypeEnum, AppActionTypeEnum } from '@/Domain/enum/ActionType.enum';
import { TierEnum } from '@/Domain/enum/Tier.enum';
import { formatRelativeTime } from '@/Domain/helper/RelativeTime.helper';
import { useGetSubscription } from '@/Domain/hooks/Subscription/useGetSubscription.hook';

type Props = {
  dataTestId: string;
  isPreview?: boolean;
  action: ActionDto | AppActionDto;
  stats?: AIActionUsageStatsDto;
  onActive?: (isActive: boolean) => void;
  onEdit?: () => void;
  buttonLabel: string;
  onMouseEnterAction?: () => void;
  onDelete?: () => void;
};

const WHITE_LIST_ACTIONS = [
  ActionTypeEnum.HttpRequest,
  ActionTypeEnum.ShopifyRemoteMcp,
  ActionTypeEnum.RemoteMcp,
  ActionTypeEnum.KnowledgeBase,
  AppActionTypeEnum.Campaign,
  AppActionTypeEnum.Handoff,
];

const adaptLucide =
  (Icon: LucideIcon): React.ComponentType<any> =>
  (props: any) => <Icon {...props} />;

const BadgeTypeConfig: Record<
  ActionTypeEnum | AppActionTypeEnum,
  { Icon: ComponentType<any>; text: string } | null
> = {
  [AppActionTypeEnum.Campaign]: null,
  [AppActionTypeEnum.Handoff]: null,
  [ActionTypeEnum.ShopifyRemoteMcp]: {
    Icon: adaptLucide(ShoppingBasket),
    text: i18n.t('actions_page:badge.shopify_mcp'),
  },
  [ActionTypeEnum.RemoteMcp]: {
    Icon: McpIcon,
    text: i18n.t('actions_page:badge.remote_mcp'),
  },
  [ActionTypeEnum.HttpRequest]: {
    Icon: ToolIcon,
    text: i18n.t('actions_page:badge.http_request_action'),
  },
  [ActionTypeEnum.KnowledgeBase]: {
    Icon: adaptLucide(BookOpen),
    text: i18n.t('actions_page:badge.knowledge_base_action'),
  },
  [ActionTypeEnum.Custom]: null,
};

const getSuccessDotColor = (stats: AIActionUsageStatsDto): string => {
  if (stats.error_count > 0) return 'bg-red-500';
  if (stats.warning_count > 0) return 'bg-amber-500';
  return 'bg-emerald-500';
};

export const ActionBox = ({
  dataTestId,
  isPreview = false,
  action,
  stats,
  onMouseEnterAction,
  onActive,
  onEdit,
  onDelete,
  buttonLabel,
}: Props) => {
  const { t } = useTranslation('actions_page');

  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();

  // App actions are plan-gated: Campaigns need lead_generation, Handoff needs human_handoff.
  const planDisabled =
    isSubscriptionFetched &&
    ((action.type === AppActionTypeEnum.Campaign && !isActivated('lead_generation')) ||
      (action.type === AppActionTypeEnum.Handoff && !isActivated('human_handoff.active')));
  // Only fetch when an upgrade pill could render — keeps the network/test cost flat.
  const { data: subscriptionData } = useGetSubscription({ enabled: planDisabled });
  // Upgrade label for the two gated app actions. lead_generation (Campaign /
  // Smart Data Gathering) is on Free and every modern tier — false only on legacy
  // `basic`/`legacy_lite`, whose upgrade path is Essential. Handoff unlocks at Basic.
  const planRequiredLabelKey: 'badge.upgrade_to_basic' | 'badge.upgrade_to_essential' | null =
    planDisabled
      ? action.type === AppActionTypeEnum.Handoff
        ? 'badge.upgrade_to_basic'
        : 'badge.upgrade_to_essential'
      : null;
  // Match the tier-based variant logic used by UpgradePlanButton: bold gradient
  // for Trial users (highest-visibility conversion ask), soft outlined for
  // anyone already paying.
  const upgradeButtonVariant =
    subscriptionData?.current_plan?.tier === TierEnum.TRIAL ||
    subscriptionData?.current_plan?.tier === TierEnum.FREE
      ? 'upgrade'
      : 'upgrade_subtle';
  const canManageAction = WHITE_LIST_ACTIONS.includes(action.type);

  const badge = BadgeTypeConfig[action.type];

  const [logsOpen, setLogsOpen] = useState(false);
  // Shopify MCP usage is attachment-derived; it has no MessageToolCall rows to log.
  const supportsCallLogs = action.type !== ActionTypeEnum.ShopifyRemoteMcp;

  const patchActionMutation = useMutation<ActionDto, undefined, PatchActionDto>({
    mutationKey: [QueryKey.actions.basic.patch],
  });
  const onUpdateIcon = (name: string, color: string) =>
    patchActionMutation.mutate({ icon: name, icon_color: color, id: action.id! });

  return (
    <div data-testid="action-box" className="h-full">
      <TooltipProvider>
        <div
          data-testid={dataTestId}
          className="flex h-full flex-col gap-5 rounded-2xl border border-border bg-card p-6"
          onMouseEnter={() => onMouseEnterAction && onMouseEnterAction()}
        >
          <header className="flex items-center gap-1 justify-between">
            <section className="flex items-center gap-4">
              <ActionBoxIcon
                icon={action.icon}
                color={action.icon_color}
                onChange={(name, color) => action.id && onUpdateIcon(name, color)}
                canSelectIcon={
                  !isPreview &&
                  Object.values(ActionTypeEnum).includes(action.type as ActionTypeEnum)
                }
              />
              {!action.is_valid && <Badge variant="error">{t('incomplete')}</Badge>}
            </section>

            <div className="flex items-start gap-2">
              {planDisabled && planRequiredLabelKey && (
                <Button
                  asChild
                  variant={upgradeButtonVariant}
                  size="xs"
                  data-testid="action-box-plan-locked-button"
                  className="!h-5 gap-1 whitespace-nowrap px-2 [&_svg]:!size-3"
                >
                  <Link to={`../${IndexPath.subscription}`}>
                    <Lock />
                    {t(planRequiredLabelKey)}
                  </Link>
                </Button>
              )}
              <Tooltip
                open={planDisabled || (!action.is_valid && !action.is_active) ? undefined : false}
              >
                <TooltipTrigger asChild>
                  <div>
                    <Switch
                      data-testid="action-box-activate-switch"
                      disabled={
                        !canManageAction || (!action.is_valid && !action.is_active) || planDisabled
                      }
                      checked={action.is_active}
                      onCheckedChange={
                        onActive ? (checked) => onActive(checked) : (_checked) => null
                      }
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  {planDisabled ? t('toast.higher_plan_required') : t('tooltip.action_invalid')}
                </TooltipContent>
              </Tooltip>
            </div>
          </header>

          <section className="space-y-1.5">
            <p className="line-clamp-2 font-semibold text-foreground">
              {action.name || '[your_title]'}
            </p>
            <p className="line-clamp-2 text-sm text-muted-foreground">
              {action.description || '[your_description]'}
            </p>
          </section>

          {badge && (
            <Badge variant="secondary" className="w-fit">
              <badge.Icon className="size-4 mr-2" /> {badge.text}
            </Badge>
          )}

          {Object.values(ActionTypeEnum).includes(action.type as ActionTypeEnum) && (
            <div className="flex items-center justify-between gap-2">
              {stats && stats.total_calls > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="flex items-center gap-1.5 text-xs text-foreground-subtle font-medium"
                      data-testid="action-stats-badge"
                    >
                      <Zap className="size-3" />
                      <span>{t('stats.calls', { count: stats.total_calls })}</span>
                      <span>&middot;</span>
                      <span className="flex items-center gap-1">
                        <span
                          className={`inline-block size-1.5 rounded-full ${getSuccessDotColor(stats)}`}
                        />
                        {Math.round(stats.success_rate)}%
                      </span>
                      {stats.last_called_at && (
                        <>
                          <span>&middot;</span>
                          <span>{formatRelativeTime(stats.last_called_at)}</span>
                        </>
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <div>
                      {t('stats.tooltip', {
                        total: stats.total_calls,
                        success: stats.success_count,
                        warning: stats.warning_count,
                        error: stats.error_count,
                      })}
                    </div>
                    <div className="text-foreground-subtle">{t('stats.tooltip_window')}</div>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <div
                  className="flex items-center gap-1.5 text-xs text-foreground-subtle italic"
                  data-testid="action-stats-empty"
                >
                  <Activity className="size-3.5" />
                  <span>{t('stats.no_calls_yet')}</span>
                </div>
              )}
              {stats && stats.total_calls > 0 && action.id != null && supportsCallLogs && (
                <Button
                  size="xs"
                  variant="ghost"
                  className="shrink-0 text-foreground-subtle"
                  data-testid="action-box-view-logs-button"
                  onClick={() => setLogsOpen(true)}
                >
                  <ListFilter className="size-4" />
                  {t('logs.view_logs_button')}
                </Button>
              )}
            </div>
          )}

          <Separator className="mt-auto" />

          <footer className="flex items-center justify-between ">
            <Button
              size="sm"
              data-testid="action-box-edit-button"
              variant="secondary"
              onClick={onEdit}
              disabled={!canManageAction}
            >
              <Pencil className="size-4" />
              {buttonLabel}
            </Button>
            {onDelete && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    data-testid="delete-action-button"
                    size="icon"
                    variant="secondary"
                    className="hover:!text-red-500"
                    onClick={onDelete}
                    disabled={!canManageAction}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('tooltip.delete_action')}</TooltipContent>
              </Tooltip>
            )}
          </footer>

          {action.id != null && supportsCallLogs && (
            <ActionLogsSheet
              actionId={action.id}
              actionName={action.name}
              open={logsOpen}
              onOpenChange={setLogsOpen}
            />
          )}
        </div>
      </TooltipProvider>
    </div>
  );
};
